import { AccordionItem, AccordionTrigger } from '@/components/shadcn/ui/accordion';
import { BusinessAttachments } from '@/entities/businessAttachments';
import { Accordion, AccordionContent } from '@radix-ui/react-accordion';
import { format } from 'date-fns';
import { FileIcon, Trash2Icon, EyeIcon } from 'lucide-react'; // Ícones de exemplo
import React, { useState, useEffect } from 'react';

interface FileCardProps {
  file: BusinessAttachments;
  onDelete: (index: number) => void;
  onVis: (filePath: string) => void;
}

const CardAttachments: React.FC<FileCardProps> = ({ file, onDelete, onVis }) => {
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [marginBottom, setMarginBottom] = useState('mb-0');
  const [filePreview, setFilePreview] = useState<string | null>(null);

  // Função para identificar o tipo de arquivo
  const getFileExtension = (path: string) => path.split('.').pop()?.toLowerCase();

  // Função para carregar a pré-visualização com base na extensão
  useEffect(() => {
    const extension = getFileExtension(file.path);

    // Se for uma imagem, mostramos a URL da imagem
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(extension || '')) {
      setFilePreview(`10.86.254.128:3001/uploads/${file.path}`); // A URL da imagem
    }
    // Se for JSON ou SQL, mostramos uma pré-visualização do texto
    else if (['json', 'sql'].includes(extension || '')) {
      fetch(`10.86.254.128:3001/uploads/${file.path}`) // Usando a URL pública para acessar o arquivo
        .then((response) => response.text())
        .then((data) => setFilePreview(data.substring(0, 200))) // Mostramos os primeiros 200 caracteres
        .catch(() => setFilePreview('Erro ao carregar o arquivo'));
    } else {
      setFilePreview(null); // Não mostramos nada para outros tipos
    }
  }, [file]);

  const handleAccordionToggle = () => {
    setAccordionOpen(!accordionOpen);
    setMarginBottom(accordionOpen ? 'mb-0' : 'mb-2');
  };

  return (
    <div className={`w-[95%] pb-2 relative ml-4 before:absolute before:ml-8 before:h-full before:w-0.5 before:bg-indigo-100 ${marginBottom} before:${marginBottom}`}>
      <Accordion type='single' collapsible>
        <AccordionItem value='item-1'>
          <div className='relative'>
            <div className='md:flex items-center mb-1 px-4'>
              <div className='flex items-center space-x-4'>
                <div className='flex items-center justify-center bg-indigo-400 w-8 h-8 rounded-full md:order-1'>
                  <FileIcon size={14} color='white' />
                </div>
              </div>
              <div className='flex text-slate-500 items-center ml-4 gap-2 pt-2'>
                <span className='text-slate-700'>{file.description}</span>
              </div>
            </div>

            <div className='flex flex-col justify-between bg-white ml-16 mx-4 p-4 rounded-lg border border-slate-100 text-slate-500 text-sm shadow-xs break-words'>
              <div className='flex justify-between'>
                <AccordionTrigger className='px-2 py-1 text-sm font-normal gap-2' onClick={handleAccordionToggle}>
                  Detalhes do Arquivo
                </AccordionTrigger>
                <div className='flex gap-1 px-2'>
                  <button title='Excluir' onClick={() => onDelete(file.id)}>
                    <Trash2Icon size={26} className='bg-indigo-50 hover:bg-indigo-200 rounded-md p-1' />
                  </button>
                  <button title='Visualizar' onClick={() => onVis(file.path)}>
                    <EyeIcon size={26} className='bg-indigo-50 hover:bg-indigo-200 rounded-md p-1' />
                  </button>
                </div>
              </div>

              {/* Exibição da pré-visualização */}
              {filePreview && (
                <div className='my-2'>
                  {['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(getFileExtension(file.path) || '') ? (
                    <img src={filePreview} alt='Preview' className='max-w-[150px] h-auto object-contain' />
                  ) : (
                    <pre className='bg-gray-100 p-2 rounded-md text-xs overflow-auto'>{filePreview}</pre>
                  )}
                </div>
              )}

              <AccordionContent>
                <div className='flex sm:flex-wrap md:flex-wrap items-center px-2 py-2 font-light text-xs gap-2'>
                  <p className='font-medium'>Caminho:</p>
                  <p>{file.path}</p>
                  <p className='font-medium'>Data de Criação:</p>
                  <p>{format(new Date(file.createdAt), 'dd/MM/yyyy')}</p>
                </div>
              </AccordionContent>
            </div>
          </div>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default CardAttachments;
